/** UK postcode area is the leading 1–2 letters of an outward code (BS1 → BS, M1 → M). */
export function areaCodeFromOutward(outward: string): string {
  const compact = outward.trim().toUpperCase().replace(/\s+/g, "");
  const match = compact.match(/^([A-Z]{1,2})/);
  return match?.[1] ?? "";
}

export function isAreaToken(token: string): boolean {
  return /^[A-Z]{1,2}$/.test(token);
}

/**
 * Split a coverage string into compact uppercase tokens (spaces and punctuation stripped).
 */
export function parseCoverageTokens(value: string): string[] {
  if (!value.trim()) {
    return [];
  }

  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const part of value.split(/[,;]+/)) {
    const token = part.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!token || seen.has(token)) {
      continue;
    }
    seen.add(token);
    tokens.push(token);
  }

  return tokens;
}

export function formatCoverageTokens(tokens: string[]): string {
  return tokens.join(", ");
}

/** Area codes present as exact tokens (BS highlights; BS1 alone does not). */
export function selectedAreaCodes(tokens: string[]): Set<string> {
  return new Set(tokens.filter(isAreaToken));
}

/**
 * Add or remove an area code. Unrelated tokens (districts, full postcodes) are left in place.
 */
export function toggleAreaToken(value: string, area: string): string {
  const areaCode = area.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!isAreaToken(areaCode)) {
    return value;
  }

  const tokens = parseCoverageTokens(value);
  if (tokens.includes(areaCode)) {
    const kept = value.split(/[,;]+/).filter((part) => {
      const cleaned = part.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
      return cleaned !== "" && cleaned !== areaCode;
    });
    return kept
      .map((part) => part.trim())
      .filter(Boolean)
      .join(", ");
  }

  const trimmed = value.trim().replace(/[,;]\s*$/, "");
  if (!trimmed) {
    return areaCode;
  }

  return `${trimmed}, ${areaCode}`;
}
